import { Model } from '@/services/api/lib'
import { toUtcDate } from '@/util/toUtcDate'

export class Cart extends Model {
  resource () {
    return 'cart/v1/carts'
  }

  validations () {
    return {
      'customer.customer_id': 'required|string',
      'items.*.product_id': 'required',
      'items.*.quantity': 'required|number|min:1',
      'items.*.price.amount': 'required|number',
      'items.*.price.currency': 'required|string',
      'items.*.sale_price.amount': 'number',
      'items.*.sale_price.currency': 'string'
    }
  }

  parse (response) {
    const defaults = {
      billing_address: {},
      shipping_address: {},
      shipping: {},
      customer: {}
    }

    const data = super.parse(response)
    return { ...defaults, ...data }
  }

  async setShippingAddress (id) {
    const { data } = await this.request({
      method: 'PUT',
      url: `${this.endpoint()}/addresses`,
      data: { shipping_address_id: id }
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async setBillingAddress (id) {
    const { data } = await this.request({
      method: 'PUT',
      url: `${this.endpoint()}/addresses`,
      data: { billing_address_id: id }
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async setShippingMethod (method) {
    if (!this.id) {
      return
    }
    const { data } = await this.request({
      method: 'PUT',
      url: `${this.endpoint()}/shipping`,
      data: { method: method }
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async addNote (note) {
    if (!this.id) {
      return
    }

    const { data } = await this.request({
      method: 'PATCH',
      url: `${this.endpoint()}/note`,
      data: { note: note }
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async deleteNote () {
    if (!this.id) {
      return
    }

    const { data } = await this.request({
      method: 'DELETE',
      url: `${this.endpoint()}/note`
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  removeItem (id) {
    this.items = this.items.filter(i => i.id !== id)

    if (!this.id) {
      return
    }

    return this.request({
      method: 'DELETE',
      url: `${this.endpoint()}/items/${id}`
    }).then(response => response.data)
  }

  async updateItem (id, params) {
    if (!this.id) {
      return
    }

    const { data } = await this.request({
      method: 'PUT',
      url: `${this.endpoint()}/items/${id}`,
      data: params
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async applyCoupon (code) {
    if (!this.id) {
      return
    }

    const { data } = await this.request({
      method: 'POST',
      url: `${this.endpoint()}/coupon`,
      data: { coupon: code }
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async removeCoupon () {
    if (!this.id) {
      return
    }

    const { data } = await this.request({
      method: 'DELETE',
      url: `${this.endpoint()}/coupon`
    })

    this._attributes = Object.assign(this._attributes, data)
    this.setup()
  }

  async syncItems (item) {
    if (this.id) {
      const { data } = await this.request({
        method: 'POST',
        url: `${this.endpoint()}/items`,
        data: { items: [ item ] }
      })

      this.items.push(item)
      this._attributes = Object.assign(this._attributes, data)
      return this.setup()
    }

    this.items.push(item)
    return this.validate()
  }

  getShippingMethods () {
    return this.request({
      method: 'GET',
      url: `${this.endpoint()}/shipping`
    }).then(response => response.data)
  }

  checkout () {
    return this.request({
      method: 'POST',
      url: `${this.endpoint()}/checkout`,
      data: {
        payment_method: this.payment,
        dob: this.customer.dob,
        requested_delivery: toUtcDate(this.requested_delivery)
      }
    }).then(response => response.data)
  }

  hasNotes (item) {
    if (!item.notes) {
      return false
    }

    let notes = item.notes
    if (!Array.isArray(notes)) {
      notes = Object.keys(notes)
    }

    return notes.length > 0
  }

  discountIncluded () {
    return this.tax_type === 'vat' && this.discount_after_tax && this.vat_in_price
  }
}
